/* Pump Operator Portal — per-job shareable tracking link + "Driver QR" for onsite crews. */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Printer, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import { printQrPage } from "@/lib/printQr";

export default function JobShareQR({ open, onOpenChange, job }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.post(`/jobs/${job.id}/share-link`);
        setUrl(`${window.location.origin}/track/job/${r.data.share_token}`);
      } catch {
        toast.error("Could not generate tracking link");
      } finally { setLoading(false); }
    })();
  }, [open, job]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Tracking link copied"); } catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Concrete tracker · ${job?.site_name || job?.customer_name || ""}`, url }); return; } catch { /* cancelled */ }
    }
    copyUrl();
  };

  const printPage = () => printQrPage({
    title: `Driver QR · ${job?.site_name || job?.customer_name || ""}`,
    tagLine: "ARMORBATCH GLOBAL · Pump Operator Portal",
    heading: job?.site_name || job?.customer_name || "",
    description: "Scan to follow every truck on this pour — live GPS, ETA and volume delivered. No login required. Valid for this job only.",
    footer: `© ARMORBATCH GLOBAL · ${job?.date || ""}`,
    url,
    svgElementId: "job-share-qr-svg",
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-industrial max-w-md" data-testid="job-share-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase">Driver QR · <span className="text-primary-orange">{job?.site_name || job?.customer_name}</span></DialogTitle>
        </DialogHeader>
        {loading && <div className="label-tag text-primary-orange pulse-orange py-4">Generating…</div>}
        {url && !loading && (
          <>
            <div className="flex justify-center py-3">
              <div className="p-4 bg-white rounded-xl border-2 border-primary-orange">
                <QRCodeSVG id="job-share-qr-svg" value={url} size={220} level="M" includeMargin={false} />
              </div>
            </div>
            <div className="mono text-[10px] text-muted-industrial break-all border border-industrial rounded-md px-2 py-1.5 text-center" data-testid="job-share-url">{url}</div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              <Button data-testid="job-share-copy" onClick={copyUrl} variant="outline" className="rounded-lg border-industrial"><Copy className="w-4 h-4" /></Button>
              <Button data-testid="job-share-native" onClick={share} variant="outline" className="rounded-lg border-industrial"><Share2 className="w-4 h-4" /></Button>
              <Button data-testid="job-share-open" onClick={() => window.open(url, "_blank")} variant="outline" className="rounded-lg border-industrial"><ExternalLink className="w-4 h-4" /></Button>
              <Button data-testid="job-share-print" onClick={printPage} className="btn-cta"><Printer className="w-4 h-4" /></Button>
            </div>
            <p className="text-[11px] text-muted-industrial mt-3 text-center">Text it to the pump operator or print it for the site foreman — they follow the trucks without an account.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
